import {createContext, use, useMemo, type ReactNode} from 'react'
import type {PublicUser} from '../../../types/public-user'
import {showAllUserMessages, type ShouldShowUserMessage} from './turn-meta'

interface AhpSessionData {
  profiles: ReadonlyMap<number, PublicUser>
}

interface AhpSessionConfig {
  shouldShowUserMessage: ShouldShowUserMessage
}

const AhpSessionDataContext = createContext<AhpSessionData | undefined>(undefined)
const AhpSessionConfigContext = createContext<AhpSessionConfig>({
  shouldShowUserMessage: showAllUserMessages,
})
const SavedAhpHistoryContext = createContext(false)

export function SavedAhpHistoryProvider({saved, children}: {saved: boolean; children: ReactNode}) {
  const inherited = use(SavedAhpHistoryContext)
  return <SavedAhpHistoryContext value={saved || inherited}>{children}</SavedAhpHistoryContext>
}

export function AhpSessionProvider({
  profiles,
  shouldShowUserMessage = showAllUserMessages,
  children,
}: {
  profiles: ReadonlyMap<number, PublicUser>
  shouldShowUserMessage?: ShouldShowUserMessage
  children: ReactNode
}) {
  const data = useMemo(() => ({profiles}), [profiles])
  const config = useMemo(() => ({shouldShowUserMessage}), [shouldShowUserMessage])

  return (
    <AhpSessionDataContext value={data}>
      <AhpSessionConfigContext value={config}>{children}</AhpSessionConfigContext>
    </AhpSessionDataContext>
  )
}

export type ActorProfileState = {kind: 'resolved'; avatar: {src: string; alt: string}} | {kind: 'loading'} | undefined

export function useActorProfileState(githubId: number | undefined): ActorProfileState {
  const data = use(AhpSessionDataContext)
  if (githubId === undefined || data === undefined) return undefined

  const profile = data.profiles.get(githubId)
  if (!profile) return {kind: 'loading'}

  return {
    kind: 'resolved',
    avatar: {
      src: profile.primaryAvatarUrl,
      alt: `${profile.login}'s avatar`,
    },
  }
}

export function useShouldShowUserMessage(): ShouldShowUserMessage {
  return use(AhpSessionConfigContext).shouldShowUserMessage
}

export function useReadOnlyAhpHistory(): boolean {
  return use(SavedAhpHistoryContext)
}
